import { Div, IRouteEvent, router } from '@codesuma/baseline'
import { Page } from '../../components/page'
import { BackIcon } from '../../components/back-icon'
import { FontRow } from './font-row'
import { NameRow } from './name-row'
import { NotificationsRow } from './notifications-row'
import services from '../../services'
import handleService from '../../services/handle'
import utils from '../../utils'
import style from './index.module.css'

export const MenuPage = () => {
    // Page() gives the shared slide transition (enter from bottom, exit up;
    // reversed on back). Override its background with the themed page tokens (these
    // are CSS vars, so the page follows light/dark live — even while cached).
    const base = Page()
    base.style({ background: 'var(--page-bg)', color: 'var(--page-fg)' })

    // currentColor so it picks up the dark page foreground.
    const back = BackIcon('currentColor')
    base.append(back)

    const title = Div('Menu')
    title.addClass(style.title)

    // Account — only meaningful once the user has a real (email) account.
    const account = Div()
    account.addClass(style.account)
    account.style({ display: 'none' })

    const accountLabel = Div('Signed in as')
    accountLabel.addClass(style.accountLabel)

    const accountEmail = Div()
    accountEmail.addClass(style.accountEmail)

    account.append(accountLabel, accountEmail)

    // Auth actions, kept up top: log in to upgrade from the anonymous identity, or
    // sign out of a real account.
    const authOptions = Div()
    authOptions.addClass(style.options)

    // Branded Google sign-in: this is the one upgrade path off the anonymous
    // identity, so it gets its own button (logo + accent-tinted surface) instead of
    // reading as just another nav row.
    const login = Div()
    login.addClass(style.oauthBtn)
    login.style({ display: 'none' })

    const googleLogo = Div()
    googleLogo.addClass(style.googleLogo)
    const loginLabel = Div('Sign in with Google')
    login.append(googleLogo, loginLabel)

    login.el.onclick = async () => {
        try {
            await services.supabase.auth.signInWithGoogle()
        } catch (err: any) {
            console.error('Failed to sign in:', err)
            alert('Login failed: ' + (err?.message || err))
        }
    }

    // Sign in with Apple — App Store Guideline 4.8 requires offering it wherever we
    // offer Google, and at least as prominently, so on iOS it sits above the Google
    // button. iOS only (native Apple sheet via signInWithApple); on other platforms
    // we show just Google. Shares the .oauthBtn shell so the two read as a pair.
    const isIOS = (window as any).Capacitor?.getPlatform?.() === 'ios'
    const appleLogin = Div()
    appleLogin.addClass(style.oauthBtn)
    appleLogin.style({ display: 'none' })

    const appleLogo = Div()
    appleLogo.addClass(style.appleLogo)
    const appleLabel = Div('Sign in with Apple')
    appleLogin.append(appleLogo, appleLabel)

    appleLogin.el.onclick = async () => {
        try {
            await services.supabase.auth.signInWithApple()
        } catch (err: any) {
            console.error('Failed to sign in:', err)
            alert('Login failed: ' + (err?.message || err))
        }
    }

    const signOut = Div('Sign out')
    signOut.addClass(style.option, style.optionDanger)
    signOut.style({ display: 'none' })
    signOut.el.onclick = async () => {
        try {
            await services.supabase.auth.signOut()
            router.goto('/')
        } catch (err: any) {
            console.error('Failed to sign out:', err)
            alert('Sign out failed: ' + (err?.message || err))
        }
    }

    if (isIOS) authOptions.append(appleLogin)
    authOptions.append(login, signOut)

    // Sharing ID — the public handle others type to share a list with you.
    const idBlock = Div()
    idBlock.addClass(style.account)

    const idLabel = Div('<b>Your sharing ID</b><br>Give this to a friend to share a list with you if you are not logged in')
    idLabel.addClass(style.accountLabel)

    // The handle and a copy button share a row; the copy icon is a currentColor
    // mask (see .copyBtn), so it picks up the dark page foreground.
    const idRow = Div()
    idRow.addClass(style.idRow)

    const idValue = Div('…')
    idValue.addClass(style.accountEmail)

    const copyBtn = Div()
    copyBtn.addClass(style.copyBtn)
    copyBtn.el.onclick = async () => {
        try {
            await navigator.clipboard.writeText(idValue.el.textContent || '')
            // Replay the pop/flash even on rapid repeat taps: clearing the class
            // and forcing a reflow restarts the CSS animation.
            copyBtn.removeClass(style.copied)
            void copyBtn.el.offsetWidth
            copyBtn.addClass(style.copied)
            setTimeout(() => copyBtn.removeClass(style.copied), 600)
        } catch (err) {
            console.error('Failed to copy sharing ID:', err)
        }
    }

    idRow.append(idValue, copyBtn)

    // The optional public name that travels with the id above.
    const nameRow = NameRow()

    idBlock.append(idLabel, idRow, nameRow)

    // Secondary nav, pinned below.
    const options = Div()
    options.addClass(style.options)

    // Ready-made public lists to save a copy of.
    const discover = Div('Discover lists')
    discover.addClass(style.option)
    discover.el.onclick = () => router.goto('/discover')

    const about = Div('About')
    about.addClass(style.option)
    about.el.onclick = () => router.goto('/about')

    // Support / privacy / account deletion. Opened as absolute papalist.app URLs via
    // the in-app browser (native) or a new tab (web) — see utils/browser. Account
    // deletion lives here in the menu so it's easy to find (App Review 5.1.1(v)).
    const linkRow = (label: string, url: string) => {
        const row = Div(label)
        row.addClass(style.option)
        row.el.onclick = () => utils.browser.openExternal(url)
        return row
    }

    options.append(
        discover,
        about,
        linkRow('Support', 'https://papalist.app/support'),
        linkRow('Privacy Policy', 'https://papalist.app/privacy'),
        linkRow('Delete account & data', 'https://papalist.app/delete'),
    )

    base.append(title, account, authOptions, idBlock, FontRow(), NotificationsRow(), options)

    base.on('enter', async (_: IRouteEvent) => {
        // Paint the locally cached handle first — the session work below hits
        // the network and can hang for a long time offline, and the handle
        // never changes anyway.
        const known = handleService.getCached()
        idValue.text(known?.handle || '…')
        nameRow.setName(known?.display_name || null)

        // Ensure an (anonymous) identity exists so we can show a sharing ID — this
        // is where a brand-new user comes to get the id they read out to a friend.
        try {
            await services.supabase.auth.ensureSession()
        } catch (err) {
            console.warn('Could not establish a session for the sharing ID:', err)
        }

        const handle = await handleService.getMine()
        if (handle) {
            idValue.text(handle.handle)
            nameRow.setName(handle.display_name)
        } else if (!known) idValue.text('unavailable')

        // Account section only applies to a real (email) account; otherwise offer
        // a way to log in and upgrade from the anonymous identity.
        const user = services.supabase.auth.getUser()
        if (user?.email) {
            accountEmail.text(user.email)
            account.style({ display: 'block' })
            signOut.style({ display: 'flex' })
            login.style({ display: 'none' })
            appleLogin.style({ display: 'none' })
        } else {
            // Reset the signed-in UI too — this page is cached and reused, so after a
            // logout the old account/email/Sign-out must be hidden again, not just
            // the Login button revealed.
            account.style({ display: 'none' })
            signOut.style({ display: 'none' })
            login.style({ display: 'flex' })
            if (isIOS) appleLogin.style({ display: 'flex' })
        }
    })

    return base
}
